import { useMemo, useState, type DragEvent } from 'react'
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react'
import { CULOARE_DEPARTAMENT, CULOARE_PRIVAT } from './constante'
import { aziIso, depasit, type CardTask } from './date'

/**
 * „Ale mele", vederea Calendar (Madalin, 19.10.2026; macheta:
 * https://claude.ai/artifact/E5onnbcP1QwbbeUVdDXKYG): task-urile omului puse pe termen, pe luna
 * sau pe saptamana. Cele fara termen stau in dreapta, ca sa nu dispara; trase pe o zi, primesc
 * termen. Tragerea e doar o scurtatura — termenul se pune si din panou, cu tastatura.
 *
 * Cheia zilei e `yyyy-mm-dd` din ISO-ul termenului, ca in panou: fara fusul orar la mijloc, un
 * termen de joi nu poate aparea miercuri.
 */

const ZILE = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du']
const LUNI = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]
/** Cate task-uri incap intr-o zi pe luna; restul, „+N", duce la saptamana ei. */
const PE_ZI = 3

const cheie = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const deLaCheie = (k: string) => {
  const [a, l, z] = k.split('-').map(Number)
  return new Date(a!, l! - 1, z!)
}
const plusZile = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
/** Luni dinaintea (sau din) ziua data. */
const luniDin = (d: Date) => plusZile(d, -((d.getDay() + 6) % 7))

export function VedereCalendar({
  taskuri,
  laDeschidere,
  laTermen,
}: {
  taskuri: CardTask[]
  laDeschidere: (id: number) => void
  /** Pune (sau muta) termenul unui task — doar pe cele pe care omul le poate edita. */
  laTermen: (t: CardTask, termen: string) => void
}) {
  const azi = aziIso()
  const [perioada, setPerioada] = useState<'luna' | 'saptamana'>('luna')
  const [reper, setReper] = useState(() => deLaCheie(azi))
  const [peste, setPeste] = useState<string | null>(null)

  const peZile = useMemo(() => {
    const m = new Map<string, CardTask[]>()
    for (const t of taskuri) {
      if (!t.termen) continue
      const k = t.termen.slice(0, 10)
      m.set(k, [...(m.get(k) ?? []), t])
    }
    // In fiecare zi: intai ce e deschis, apoi ce s-a terminat.
    for (const l of m.values())
      l.sort((a, b) => Number(a.stare === 'done') - Number(b.stare === 'done') || a.id - b.id)
    return m
  }, [taskuri])
  const faraTermen = taskuri.filter((t) => !t.termen && t.stare !== 'done')

  const zile = useMemo(() => {
    if (perioada === 'saptamana') {
      const l = luniDin(reper)
      return Array.from({ length: 7 }, (_, i) => plusZile(l, i))
    }
    const prima = luniDin(new Date(reper.getFullYear(), reper.getMonth(), 1))
    const ultima = new Date(reper.getFullYear(), reper.getMonth() + 1, 0)
    const n = Math.ceil(((ultima.getTime() - prima.getTime()) / 86_400_000 + 1) / 7) * 7
    return Array.from({ length: n }, (_, i) => plusZile(prima, i))
  }, [perioada, reper])

  const muta = (pas: number) =>
    setReper((r) =>
      perioada === 'luna' ? new Date(r.getFullYear(), r.getMonth() + pas, 1) : plusZile(r, 7 * pas),
    )
  const titlu =
    perioada === 'luna'
      ? `${LUNI[reper.getMonth()]} ${reper.getFullYear()}`
      : (() => {
          const l = luniDin(reper)
          const d = plusZile(l, 6)
          return `${l.getDate()} ${LUNI[l.getMonth()]!.slice(0, 3).toLowerCase()} – ${d.getDate()} ${LUNI[d.getMonth()]!.slice(0, 3).toLowerCase()} ${d.getFullYear()}`
        })()

  const lasa = (e: DragEvent, ziua: string) => {
    e.preventDefault()
    setPeste(null)
    const id = Number(e.dataTransfer.getData('text/task'))
    const t = taskuri.find((x) => x.id === id)
    if (t && t.poateEdita && t.termen?.slice(0, 10) !== ziua) laTermen(t, ziua)
  }

  return (
    <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1fr)_260px]">
      <section
        className="min-w-0 overflow-hidden rounded-caseta border border-b4l-border bg-b4l-surface"
        aria-label="Calendar"
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-b4l-border px-3 py-2.5">
          <h2 className="text-corp font-semibold text-b4l-text">{titlu}</h2>
          <button
            type="button"
            onClick={() => setReper(deLaCheie(azi))}
            className="rounded-md border border-b4l-border px-2 py-0.5 text-mic text-b4l-text hover:bg-b4l-bg"
          >
            Azi
          </button>
          <span className="flex gap-1">
            <button
              type="button"
              onClick={() => muta(-1)}
              aria-label={perioada === 'luna' ? 'Luna trecută' : 'Săptămâna trecută'}
              className="rounded-md border border-b4l-border p-1 text-b4l-text hover:bg-b4l-bg"
            >
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => muta(1)}
              aria-label={perioada === 'luna' ? 'Luna viitoare' : 'Săptămâna viitoare'}
              className="rounded-md border border-b4l-border p-1 text-b4l-text hover:bg-b4l-bg"
            >
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </span>
          <span
            className="ml-auto inline-flex gap-0.5 rounded-caseta bg-b4l-bg p-0.5"
            role="group"
            aria-label="Perioadă"
          >
            {(
              [
                ['luna', 'Lună'],
                ['saptamana', 'Săptămână'],
              ] as const
            ).map(([k, e]) => (
              <button
                key={k}
                type="button"
                aria-pressed={perioada === k}
                onClick={() => setPerioada(k)}
                className={`rounded-md px-2.5 py-1 text-mic font-medium ${
                  perioada === k
                    ? 'bg-b4l-surface text-b4l-text shadow-sm'
                    : 'text-b4l-text-muted hover:text-b4l-text'
                }`}
              >
                {e}
              </button>
            ))}
          </span>
        </div>
        <div className="grid grid-cols-7 text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
          {ZILE.map((z) => (
            <div key={z} className="px-2 py-1.5">
              {z}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {zile.map((d, i) => {
            const k = cheie(d)
            const alta = perioada === 'luna' && d.getMonth() !== reper.getMonth()
            const lista = peZile.get(k) ?? []
            const vazute = perioada === 'luna' ? lista.slice(0, PE_ZI) : lista
            const rest = lista.length - vazute.length
            return (
              <div
                key={k}
                onDragOver={(e) => {
                  e.preventDefault()
                  setPeste(k)
                }}
                onDragLeave={() => setPeste((p) => (p === k ? null : p))}
                onDrop={(e) => lasa(e, k)}
                className={`grid min-w-0 content-start gap-1 border-t border-b4l-border p-1 ${
                  i % 7 ? 'border-l' : ''
                } ${perioada === 'luna' ? 'min-h-[92px] max-sm:min-h-[64px]' : 'min-h-[220px]'} ${
                  alta ? 'bg-b4l-bg-soft' : i % 7 >= 5 ? 'bg-b4l-bg-soft/50' : ''
                } ${peste === k ? 'bg-b4l-blue-light' : ''}`}
              >
                <span
                  className={`justify-self-start text-mic tabular-nums ${
                    k === azi
                      ? 'grid h-5 min-w-5 place-items-center rounded-full bg-b4l-blue px-1 font-bold text-white'
                      : alta
                        ? 'text-b4l-text-dim'
                        : 'text-b4l-text-muted'
                  }`}
                  aria-label={k === azi ? `Azi, ${d.getDate()}` : undefined}
                >
                  {d.getDate()}
                </span>
                {vazute.map((t) => (
                  <Eveniment key={t.id} t={t} laDeschidere={laDeschidere} />
                ))}
                {rest > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setReper(d)
                      setPerioada('saptamana')
                    }}
                    className="justify-self-start rounded px-1 text-eticheta font-semibold text-b4l-blue hover:underline"
                  >
                    +{rest} {rest === 1 ? 'altul' : 'altele'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <aside
        className="grid content-start gap-2 rounded-caseta border border-b4l-border bg-b4l-surface p-3"
        aria-label="Fără termen"
      >
        <h2 className="text-corp font-semibold text-b4l-text">
          Fără termen <span className="tabular-nums text-b4l-text-dim">· {faraTermen.length}</span>
        </h2>
        <p className="text-mic text-b4l-text-muted">
          Trage unul pe o zi ca să-i dai termen, sau pune-l din panou.
        </p>
        {faraTermen.map((t) => (
          <Eveniment key={t.id} t={t} laDeschidere={laDeschidere} mare />
        ))}
        {faraTermen.length === 0 && <p className="text-mic text-b4l-text-dim">Toate au termen.</p>}
      </aside>
    </div>
  )
}

function Eveniment({
  t,
  laDeschidere,
  mare = false,
}: {
  t: CardTask
  laDeschidere: (id: number) => void
  mare?: boolean
}) {
  const culoare = t.departament ? CULOARE_DEPARTAMENT[t.departament].bara : CULOARE_PRIVAT.bara
  const intarziat = depasit(t)
  const gata = t.stare === 'done'
  return (
    <button
      type="button"
      draggable={t.poateEdita && !gata}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/task', String(t.id))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onClick={() => laDeschidere(t.id)}
      title={t.titlu}
      className={`flex w-full min-w-0 items-center gap-1 rounded border-l-[3px] px-1.5 text-left ${culoare} ${
        mare ? 'py-1.5 text-corp' : 'py-0.5 text-eticheta'
      } ${
        intarziat
          ? 'bg-b4l-red-light font-semibold text-b4l-red-dark'
          : gata
            ? 'bg-b4l-bg-soft text-b4l-text-dim line-through'
            : 'bg-b4l-bg-soft text-b4l-text hover:bg-b4l-bg'
      } focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue`}
    >
      {t.privat && <Lock size={10} className="shrink-0" aria-label="Privat" />}
      <span className="truncate">{t.titlu}</span>
    </button>
  )
}
